// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SavingsCalculator from '../components/SavingsCalculator';
import { useNavigate } from 'react-router-dom';
import { trackBeginSubscription } from '../utils/tracking';

/**
 * PricePAL Pricing Page with 7-Day Free Trial
 * Transparent pricing display with trial messaging
 * Shows FREE, INDIVIDUAL, FAMILY tiers + Founder's Club
 */
const PricingPage = ({ onBack, userId }) => {
  const navigate = useNavigate();
  const [pricingTiers, setPricingTiers] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [trialStatus, setTrialStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [founderCapacity, setFounderCapacity] = useState(null);
  // iter170 Phase D — first-billing preview per (tier, billing_cycle).
  // Populated once tiers arrive; used to display the exact date the card
  // will first be charged so users know before they hit Stripe.
  const [firstBilling, setFirstBilling] = useState({});
  // Safe initial state — limits are visible, but counts stay null so the UI
  // renders a skeleton placeholder (never a misleading "0/50") until the
  // /api/pricing/phase response arrives.
  const [pricingPhase, setPricingPhase] = useState({
    phase: 'phase1',
    vip_count: null, vip_limit: 200, vip_full: false,
    early_count: null, early_limit: 750, early_full: false,
    alpha_count: null, alpha_limit: 50, alpha_full: false,
    show_founder_tiers: true, show_coupon_ui: false,
  });
  const [phaseLoaded, setPhaseLoaded] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponApplyingTier, setCouponApplyingTier] = useState(null);
  const [couponError, setCouponError] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchPricingTiers();
    checkAuthStatus(); // Check auth via API instead of localStorage
    fetchFounderCapacity();
    fetchPricingPhase();
    // Auto-apply LAUNCH1 when arriving from email CTA: /pricing?coupon=LAUNCH1
    // Binds to INDIVIDUAL (Premium) by default — user can re-bind to FAMILY by
    // clicking that card's coupon input. Only takes visible effect once
    // pricingPhase.show_coupon_ui is true (Phase 2+).
    try {
      const params = new URLSearchParams(window.location.search);
      const couponParam = (params.get('coupon') || '').toUpperCase().trim();
      if (couponParam === 'LAUNCH1') {
        setCouponInput('LAUNCH1');
        setCouponApplyingTier('INDIVIDUAL');
      }
    } catch (e) { /* SSR / no-window guard */ }
  }, []);

  const fetchFounderCapacity = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/founder-capacity/status`);
      setFounderCapacity(response.data);
    } catch (e) { /* Show all tiers as fallback */ }
  };

  const fetchPricingPhase = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/pricing/phase`);
      setPricingPhase(response.data);
    } catch (e) { /* Default to phase1 (founder tiers visible, no coupon) */ }
    finally { setPhaseLoaded(true); }
  };

  const fetchPricingTiers = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/pricing/compare`);
      setPricingTiers(response.data.tiers);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      setLoading(false);
    }
  };

  // Check authentication status via API (works even when localStorage is blocked)
  const checkAuthStatus = async () => {
    try {
      // This endpoint uses httpOnly cookies, so it works even with tracking prevention
      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data && response.data.id) {
        setCurrentUser(response.data);
        
        // Also try to save to localStorage for other pages (may fail silently)
        try {
          localStorage.setItem('user_id', response.data.id);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (e) {
        }
        
        // Fetch trial status if authenticated
        fetchTrialStatus(response.data.id);
      }
    } catch (error) {
      // Not authenticated - that's okay, user can still view pricing
    }
  };

  const fetchTrialStatus = async (userIdToFetch) => {
    // Use provided userId or try localStorage as fallback
    let storedUserId = userIdToFetch;
    
    if (!storedUserId) {
      try {
        storedUserId = localStorage.getItem('user_id');
      } catch (e) {
        // localStorage blocked
      }
    }
    
    // Don't fetch if no user is logged in or if it's a placeholder ID
    if (!storedUserId || storedUserId === 'user_1' || storedUserId === 'null' || storedUserId === 'undefined') {
      return;
    }

    try {
      const response = await axios.get(`${backendUrl}/api/users/${storedUserId}/trial-status`, {
        withCredentials: true
      });
      setTrialStatus(response.data);
    } catch (error) {
      // Silently handle 404 - user may not exist or not be logged in
      if (error.response?.status !== 404) {
        console.error('Error fetching trial status:', error);
      }
    }
  };

  // iter170 Phase D — fetch first-billing preview for each paid tier + the
  // currently selected billing cycle. Runs whenever the tier list or
  // billing cycle changes.
  useEffect(() => {
    if (!pricingTiers || pricingTiers.length === 0) return;
    const paidKeys = pricingTiers
      .filter(t => t.monthly_price > 0)
      .map(t => ({ tierId: { INDIVIDUAL: 'individual', FAMILY: 'family', COMMUNITY: 'community', VIP_FOUNDER: 'vip_founder', EARLY_FOUNDER: 'early_founder' }[t.key] || t.key.toLowerCase(), tierKey: t.key }));
    (async () => {
      const results = {};
      await Promise.all(paidKeys.map(async ({ tierId, tierKey }) => {
        try {
          const res = await axios.get(`${backendUrl}/api/subscription/first-billing-preview`, {
            params: { tier_id: tierId, billing_cycle: billingCycle },
            withCredentials: true,
          });
          results[tierKey] = res.data;
        } catch (_e) { /* preview is best-effort */ }
      }));
      setFirstBilling(results);
    })();
  }, [pricingTiers, billingCycle, backendUrl]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span>
              </span>
            </button>
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Home</button>
              <button onClick={() => navigate('/pricing')} className="text-blue-600 font-bold">Pricing</button>
              <button onClick={() => navigate('/blog')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Blog</button>
              <button onClick={() => navigate('/app')} className="px-6 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg hover:shadow-lg transition">Sign In</button>
            </div>
            <div className="flex md:hidden items-center gap-3">
              <button onClick={() => navigate('/app')} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-bold rounded-lg">Sign In</button>
              <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-blue-600 font-semibold whitespace-nowrap">← Home</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Compare smarter. Shop smarter. Spend less.
          </p>

          {/* iter170 Phase D — Card-Upfront banner. Only shown when the
              first-billing preview endpoint reports the trial is ACTUALLY
              available on Individual or Family (the tiers rendered on this
              page). When TRIAL_REQUIRES_CARD is off, those come back with
              trial_days=0 and the banner falls back to a simpler
              subscription message so we never promise a trial we can't
              honour. Community is intentionally excluded from this check
              because it always has a 7-day trial regardless of the flag. */}
          {(() => {
            const paidTierHasTrial = ['INDIVIDUAL', 'FAMILY'].some(k => firstBilling[k] && firstBilling[k].trial_days > 0);
            const cycleLabel = billingCycle === 'annual' ? 'year' : 'month';
            const premiumPeriodPrice = billingCycle === 'annual' ? '£19.99/year' : '£1.99/month';
            const familyPeriodPrice = billingCycle === 'annual' ? '£29.99/year' : '£2.99/month';
            if (paidTierHasTrial) {
              return (
                <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-2xl p-6 mb-8 max-w-4xl mx-auto shadow-2xl" data-testid="trial-banner">
                  <div className="flex items-center justify-center space-x-3">
                    <span className="text-4xl">🎉</span>
                    <div className="text-left">
                      <h2 className="text-2xl font-bold">Start with Premium — 7 Days Free</h2>
                      <p className="text-green-100 text-sm mt-1">
                        Unlimited scans and price alerts, Full Fuel Finder, Kitchen AI and receipt scanning.
                      </p>
                      <p className="text-green-100 text-sm mt-2">
                        Card required · No charge today · {premiumPeriodPrice} after your trial · Cancel anytime
                      </p>
                      <p className="text-green-200 text-xs mt-2">
                        Family {familyPeriodPrice} · Up to 4 members
                      </p>
                      {trialStatus && trialStatus.on_trial && (
                        <p className="text-yellow-200 text-sm font-semibold mt-2">
                          ⏰ Your trial: {trialStatus.days_remaining} days remaining
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            // Flag off — no trial available yet in production. Still lead with
            // Premium as the primary paid proposition per spec §2 tone, but
            // adapt the copy to the current no-trial reality.
            return (
              <div className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-2xl p-6 mb-8 max-w-4xl mx-auto shadow-2xl" data-testid="subscribe-banner">
                <div className="flex items-center justify-center space-x-3">
                  <span className="text-4xl">🛒</span>
                  <div className="text-left">
                    <h2 className="text-2xl font-bold">Start with Premium — {premiumPeriodPrice}</h2>
                    <p className="text-blue-100 text-sm mt-1">
                      Unlimited scans and price alerts, Full Fuel Finder, Kitchen AI and receipt scanning.
                    </p>
                    <p className="text-blue-100 text-sm mt-2">
                      Cancel anytime — you keep paid access until the end of your billing period.
                    </p>
                    <p className="text-blue-200 text-xs mt-2">
                      Family {familyPeriodPrice} · Up to 4 members
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-full p-1 shadow-md">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                Save 16%
              </span>
            </button>
          </div>
        </div>

        {/* NHS Support Banner */}
        <div className="flex items-center justify-center gap-3 mb-6 bg-blue-50 border border-blue-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/></svg>
          <p className="text-sm text-blue-800 font-medium">
            Proudly supporting NHS staff and Blue Light Card holders with exclusive pricing
          </p>
          <a href="/nhs" className="text-blue-600 hover:text-blue-700 text-sm font-semibold whitespace-nowrap underline">
            NHS rates &rarr;
          </a>
        </div>

        {/* Founder capacity bars HIDDEN — 2026-06 user directive:
            "I want all beta test to be hidden from the pricing pages now so that
            only free trial, premium and family are shown as prices and the
            community pricing." Section kept commented for future re-enable.
        {pricingPhase?.phase === 'phase1' && (pricingPhase?.vip_limit || pricingPhase?.early_limit || pricingPhase?.alpha_limit) && (
          <div id="founder-tiers" className="mb-10 bg-white rounded-2xl shadow-md border border-gray-100 p-5" data-testid="founder-capacity-bars">
            ... founder bars block ...
          </div>
        )}
        */}

        {/* iter170 spec compliance (Doc-1 §4, §18, §47 + Doc-2 §15):
            Free must NOT appear as an equal primary acquisition card. The
            main grid shows Premium (hero / Most Popular) + Family only.
            Free is explained in a separate, visually secondary "Prefer
            not to enter a card?" panel below the grid. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 max-w-4xl mx-auto items-stretch">
          {pricingTiers
            .filter(tier => ['INDIVIDUAL', 'FAMILY'].includes(tier.key))
            .sort((a, b) => {
              const order = { INDIVIDUAL: 0, FAMILY: 1 };
              return (order[a.key] ?? 99) - (order[b.key] ?? 99);
            })
            .map((tier, index) => (
            <div
              key={tier.key}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:-translate-y-2 flex flex-col ${
                tier.is_popular
                  ? 'ring-4 ring-blue-500 md:scale-[1.02]'
                  : tier.key === 'FAMILY'
                    ? 'ring-4 ring-indigo-400'
                    : ''
              }`}
            >
              {/* Ribbon slot — reserves a consistent header stripe on BOTH cards
                  so titles/prices align across Premium (Most Popular) and Family
                  (Best for Households). Fixes iter171 alignment audit; iter172f
                  extended Family with its own indigo-violet ribbon to give it a
                  matching visual weight without stealing the "Most Popular" tag. */}
              <div className={`text-white text-center py-2 font-semibold text-sm ${
                tier.is_popular
                  ? 'bg-gradient-to-r from-blue-600 to-green-600'
                  : tier.key === 'FAMILY'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600'
                    : 'bg-transparent text-transparent select-none'
              }`} aria-hidden={!tier.is_popular && tier.key !== 'FAMILY'}>
                {tier.is_popular
                  ? 'Most Popular'
                  : tier.key === 'FAMILY'
                    ? 'Best for Households'
                    : '\u00A0'}
              </div>
              {/* Founder Badges removed 2026-06 — beta tiers hidden from public pricing */}

              <div className="p-8 flex flex-col flex-1">
                {/* Tier Name */}
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {tier.name}
                </h3>

                {/* iter172f: value ribbon on Family — indigo-violet to match
                    the new card border + top ribbon; kept the household emoji
                    for warmth. */}
                {tier.key === 'FAMILY' && (
                  <div
                    className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs font-semibold w-fit"
                    data-testid="family-value-ribbon"
                  >
                    <span>👨‍👩‍👧‍👦</span>
                    <span>4 members · one bill · shared savings</span>
                  </div>
                )}

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-5xl font-bold text-gray-900">
                      £{Number(billingCycle === 'monthly' ? tier.monthly_price : tier.annual_price).toFixed(2)}
                    </span>
                    <span className="text-gray-500 ml-2">
                      /{billingCycle === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  <p className="text-sm text-emerald-700 mt-1 font-semibold" data-testid={`trial-subtitle-${tier.key.toLowerCase()}`}>
                    {firstBilling[tier.key]?.trial_days > 0 ? '7-day free trial' : 'Cancel anytime'}
                  </p>
                  {billingCycle === 'annual' && tier.annual_price > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      £{(tier.annual_price / 12).toFixed(2)}/month — Save £{((tier.monthly_price * 12) - tier.annual_price).toFixed(2)}
                    </p>
                  )}
                  {/* Phase 2 LAUNCH1 callout — Individual + Family only, monthly only */}
                  {pricingPhase?.show_coupon_ui && billingCycle === 'monthly' && (
                    <p className="text-sm text-emerald-700 mt-1 font-semibold" data-testid={`launch1-callout-${tier.key.toLowerCase()}`}>
                      Try Premium for £1 (first month) — code <span className="font-mono">LAUNCH1</span>
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* LAUNCH1 coupon input — Individual + Family only, monthly only */}
                {pricingPhase?.show_coupon_ui && billingCycle === 'monthly' && (
                  <div className="mb-3" data-testid={`coupon-block-${tier.key.toLowerCase()}`}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Have a promo code?</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        data-testid={`coupon-input-${tier.key.toLowerCase()}`}
                        value={couponApplyingTier === tier.key ? couponInput : ''}
                        onChange={(e) => { setCouponApplyingTier(tier.key); setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                        placeholder="LAUNCH1"
                        maxLength={20}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    {couponApplyingTier === tier.key && couponInput.trim() && couponInput.trim().toUpperCase() !== 'LAUNCH1' && (
                      <p className="text-xs text-red-500 mt-1">Code not recognised.</p>
                    )}
                    {couponApplyingTier === tier.key && couponInput.trim().toUpperCase() === 'LAUNCH1' && (
                      <p className="text-xs text-emerald-600 mt-1">✓ £1 first month will be applied at checkout.</p>
                    )}
                    {couponError && couponApplyingTier === tier.key && (
                      <p className="text-xs text-red-500 mt-1" data-testid={`coupon-error-${tier.key.toLowerCase()}`}>{couponError}</p>
                    )}
                  </div>
                )}

                {/* CTA Button — grid filter guarantees only INDIVIDUAL + FAMILY reach here */}
                <button
                  onClick={async () => {
                    // Paid tier — Go directly to Stripe checkout (works with or without login)
                    try {
                      const tierMapping = { 'INDIVIDUAL': 'individual', 'FAMILY': 'family' };
                      const tierKey = tierMapping[tier.key];

                      // Apply LAUNCH1 coupon iff Phase 2 + monthly + user typed it.
                      const couponEligible = pricingPhase?.show_coupon_ui &&
                        billingCycle === 'monthly' &&
                        couponApplyingTier === tier.key &&
                        couponInput.trim().toUpperCase() === 'LAUNCH1';

                      const response = await axios.post(
                        `${backendUrl}/api/stripe-payments/checkout/create`,
                        {
                          tier_id: tierKey,
                          billing_cycle: billingCycle,
                          origin_url: window.location.origin,
                          ...(couponEligible ? { coupon_code: 'LAUNCH1' } : {}),
                        },
                        { withCredentials: true }
                      );

                      if (response.data.success && response.data.checkout_url) {
                        // iter158 — Fire `begin_subscription` (GA4 recommended
                        // event) BEFORE redirecting to Stripe. Reporting only.
                        const price = billingCycle === 'monthly' ? tier.monthly_price : tier.annual_price;
                        trackBeginSubscription(currentUser?.id || null, tier.key, price, 'GBP');
                        window.location.href = response.data.checkout_url;
                      } else {
                        throw new Error(response.data.error || 'Failed to create checkout');
                      }
                    } catch (error) {
                      console.error('Checkout error:', error);
                      const detail = error?.response?.data?.detail;
                      if (detail) setCouponError(detail);
                      // Fallback: redirect to login with checkout intent
                      try {
                        localStorage.setItem('pendingCheckout', JSON.stringify({
                          tier: tier.key, tierName: tier.name,
                          billingCycle: billingCycle,
                          price: billingCycle === 'monthly' ? tier.monthly_price : tier.annual_price
                        }));
                      } catch (e) {}
                      navigate(`/app?action=login&reason=checkout&tier=${tier.key}&billing=${billingCycle}`);
                    }
                  }}
                  className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all ${
                    tier.is_popular
                      ? 'bg-gradient-to-r from-blue-600 to-green-600 text-white hover:scale-105'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  data-testid={`pricing-cta-${tier.key.toLowerCase()}`}
                >
                  {(() => {
                    if (trialStatus && (trialStatus.on_trial || trialStatus.trial_used || trialStatus.trial_expired)) {
                      return `Upgrade to ${tier.name}`;
                    }
                    const trialAvailable = firstBilling[tier.key]?.trial_days > 0;
                    if (!trialAvailable) {
                      return `Subscribe · £${Number(billingCycle === 'monthly' ? tier.monthly_price : tier.annual_price).toFixed(2)}/${billingCycle === 'monthly' ? 'mo' : 'yr'}`;
                    }
                    return tier.key === 'FAMILY' ? 'Start Family Free Trial' : 'Start 7-Day Free Trial';
                  })()}
                </button>

                {/* Post-CTA supporting copy — always tier & billing-cycle aware.
                    Per spec §3/§4/§5/§6: never leave "£1.99/month after your
                    trial" showing when Annual is selected; always show
                    the first-payment date when we can calculate it. */}
                {(() => {
                  const periodPrice = billingCycle === 'annual'
                    ? Number(tier.annual_price).toFixed(2)
                    : Number(tier.monthly_price).toFixed(2);
                  const periodLabel = billingCycle === 'annual' ? 'year' : 'month';
                  const fb = firstBilling[tier.key];
                  const trialAvailable = fb?.trial_days > 0;
                  return (
                    <div
                      className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-center"
                      data-testid={`first-billing-preview-${tier.key.toLowerCase()}`}
                    >
                      <p className="text-xs text-blue-900 leading-relaxed">
                        {trialAvailable ? (
                          <>
                            <strong>Card required · No charge today · £{periodPrice}/{periodLabel} after 7 days · Cancel anytime</strong>
                            {fb?.first_billing_at_human && (
                              <><br />First payment: <strong>{fb.first_billing_at_human}</strong></>
                            )}
                          </>
                        ) : (
                          <>
                            £{periodPrice}/{periodLabel} charged today · Cancel anytime
                          </>
                        )}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        {/* Concession discount discoverability — single entry-point for
            NHS / Blue Light / Student / Teacher (audit 2026-02-12). */}
        <div className="text-center mb-12" data-testid="pricing-community-cta">
          <p className="text-gray-600 text-sm mb-3">
            Are you NHS staff, a Blue Light Card holder, a student, or a teacher?
          </p>
          <button
            onClick={() => navigate('/community')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded-full border border-blue-200 transition"
            data-testid="pricing-community-link"
          >
            Get the £1.00/month Community rate →
          </button>
        </div>

        {/* Savings Calculator */}
        <SavingsCalculator onStartTrial={() => navigate('/portal')} />

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">How does the 7-day free trial work?</h4>
              <p className="text-gray-600">
                Choose Premium or Family and enjoy full access to your plan for 7 days free. A valid card is required when you sign up, but you won't be charged during your trial.
                <br /><br />
                Before you confirm, we'll show you the exact date your first payment will be taken. If you continue after the trial, your subscription will automatically start at £1.99/month for Premium or £2.99/month for Family.
                <br /><br />
                You can cancel anytime from Settings. If you cancel during your free trial, you won't be charged.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">What's the difference between Premium and Family?</h4>
              <div className="text-gray-600 space-y-2 text-sm">
                <p>
                  <strong>Premium (£1.99/mo)</strong> covers one account with unlimited scans, price alerts, Full Fuel Finder, Kitchen AI and receipt scanning/submissions.
                </p>
                <p>
                  <strong>Family (£2.99/mo)</strong> gives your whole household one bill and adds features Premium doesn't have:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Up to 4 family members</strong> — each with their own login and profile</li>
                  <li><strong>Shared household shopping lists</strong> — live-synced across everyone's phones so nothing gets bought twice</li>
                  <li><strong>Kitchen AI household mode</strong> — recipes and portions auto-scale based on age groups (kids vs teens vs adults)</li>
                  <li><strong>Combined family savings tracker</strong> — see the whole household's monthly savings in one place</li>
                  <li><strong>Just 75p per member per month</strong> — 62% cheaper than four Premium subs (£7.96/mo)</li>
                </ul>
                <p className="text-xs text-gray-500 italic pt-1">
                  Both plans include the 7-day free trial with card required.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">Am I eligible for the £1.00 Community rate?</h4>
              <p className="text-gray-600">
                Yes if you're NHS staff, a Blue Light Card holder, a student (NUS/TOTUM), or a teacher (TRN). Click the Community link above and we'll verify your ID — the rate of <strong>£1.00/mo</strong> is locked in for as long as you stay subscribed.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">Can I switch plans later?</h4>
              <p className="text-gray-600">
                Yes — upgrade, downgrade, or cancel anytime from Settings or via your Stripe billing portal. Changes take effect immediately and any unused time is fairly pro-rated.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">What payment methods do you accept?</h4>
              <p className="text-gray-600">
                All major credit and debit cards (Visa, Mastercard, Amex) via our secure payment partner Stripe. Direct debit is also available for UK customers.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">Is my data secure?</h4>
              <p className="text-gray-600">
                Absolutely. We use bank-level encryption, never sell your data to third parties, and comply with UK GDPR. Payments are handled securely by Stripe — we never see your card details.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">How do I cancel?</h4>
              <p className="text-gray-600">
                You can cancel anytime from Settings in the app, or via your Stripe billing portal. Your access continues until the end of your current billing period. Your data is never deleted.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h4 className="font-bold text-gray-800 mb-2">Is there a Free plan?</h4>
              <p className="text-gray-600">
                Yes. PricePAL also has a Standard Free option with limited access to selected features. It includes 5 product scans per day, up to 3 active price alerts and 2 receipt submissions per month, together with Basic Fuel Finder access.
                <br /><br />
                The Free plan does not require a payment card. Premium and Family provide the full PricePAL experience, including unlimited scanning and alerts, Full Fuel Finder and Kitchen AI.
              </p>
            </div>
          </div>
        </div>

        {/* Back Navigation */}
        <div className="mt-12 text-center">
          <button
            onClick={() => {
              // If onBack prop is provided (from UserApp), use it
              if (onBack) {
                onBack();
              } else {
                // Otherwise, check URL context
                const pathname = window.location.pathname;
                if (pathname.includes('/app')) {
                  navigate('/app');
                } else if (pathname.includes('/admin')) {
                  navigate('/admin');
                } else {
                  // Use browser back or go to home
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    navigate('/');
                  }
                }
              }
            }}
            className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      </div>
    </div>
  );
};

export default PricingPage;
